import { ReactElement, useEffect, useRef, useState } from 'react'
import { AsyncThunkAction, AsyncThunkConfig } from '@reduxjs/toolkit'
import { useForm } from '@tanstack/react-form'

import axios, { pluckError } from 'src/utilities/http-client'
import {
  CancelIcon,
  CorrectIcon,
  InfoIcon,
  SubmitIcon,
} from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchCourses,
  fetchRecertificationCycleCategories,
  fetchSections,
  selectCoursesStatus,
  selectRecertificationCategories,
  selectRecertificationCategoriesStatus,
  selectSections,
  selectSectionsStatus,
  selectSortedCourses,
} from 'src/v3/profile/profile.slice'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { RecertificationCycleCourse } from 'src/v3/core/types'
import CourseDetailModal from 'src/v3/manage/components/course-detail-modal'

type CourseFormProps = {
  cycleID: string
  course?: Partial<RecertificationCycleCourse>
  refetchCycles: AsyncThunkAction<any, any, AsyncThunkConfig>
  open: boolean
  setOpen: (status: boolean) => void
}

export let CourseForm = ({
  cycleID,
  course,
  refetchCycles,
  open,
  setOpen,
}: CourseFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()
  let [detailCourse, setDetailCourse] = useState<{
    id: string
    name: string
  } | null>(null)
  // Controlled so the course list keeps its search and scroll position while
  // a course's details are open over it.
  let [courseListOpen, setCourseListOpen] = useState(false)
  let [courseSearch, setCourseSearch] = useState('')
  // Narrows the course list to one of the platform's sections (Airway, Medical, …). Kept while
  // the cycle is open so consecutive adds can stay in one category.
  let [sectionFilter, setSectionFilter] = useState('all')
  // Set while the list is left open for a course's details. The input resyncs
  // to the selected course as focus leaves and returns, so those resets are
  // ignored until the user acts on the list again or it actually closes.
  let keepCourseSearch = useRef(false)

  let courses = useAppSelector(selectSortedCourses(cycleID))
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let sections = useAppSelector(selectSections)
  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let allCategories = useAppSelector(selectRecertificationCategories)
  let categoriesStatus = useAppSelector(selectRecertificationCategoriesStatus)

  let [saving, setSaving] = useState(false)

  let categories = allCategories[cycleID || ''] || []
  let allCourses = categories.flatMap((cat) => cat.courses)
  let allCoursesFiltered = allCourses.filter(
    (co) => co.courseID === course?.courseID,
  )
  let availableCourses = courses.filter(
    (c) =>
      course?.courseID === c.id ||
      !allCourses.some((rcc) => rcc.courseID === c.id),
  )
  let availableSections = sections
    .filter((s) => availableCourses.some((c) => c.sectionID === s.id))
    .toSorted((a, b) => a.name.localeCompare(b.name))
  // The remembered filter, ignored once its section has no courses left to add.
  let activeSectionFilter = availableSections.some(
    (s) => s.id === sectionFilter,
  )
    ? sectionFilter
    : 'all'

  useAutoFetch(coursesStatus, fetchCourses)
  useAutoFetch(sectionsStatus, fetchSections)

  useEffect(() => {
    if (
      cycleID &&
      (!categoriesStatus[cycleID] ||
        categoriesStatus[cycleID] === 'uninitialized')
    ) {
      dispatch(fetchRecertificationCycleCategories(cycleID))
    }
  }, [cycleID, categoriesStatus])

  let form = useForm({
    defaultValues: {
      course: course?.courseID || '',
      categories: !allCoursesFiltered.length
        ? [
            {
              id: undefined,
              category: course?.categoryID || 'unselected',
              hours: '',
            },
          ]
        : allCoursesFiltered.map((co) => ({
            id: co.id,
            category: co?.categoryID || 'unselected',
            hours: co.hours?.toString() || '',
          })),
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      let ids = value.categories.map((catJoin) => catJoin.id)
      Promise.all([
        ...allCoursesFiltered
          .filter((co) => !ids.includes(co.id))
          .map((co) =>
            axios.delete(`/api/profile/recertification-cycle/course/${co.id}`),
          ),
        ...value.categories.map((cat) =>
          !cat.id
            ? axios.post<RecertificationCycleCourse>(
                `/api/profile/recertification-cycle/course`,
                {
                  courseID: value.course,
                  categoryID: cat.category,
                  hours: cat.hours,
                },
              )
            : axios.put<RecertificationCycleCourse>(
                `/api/profile/recertification-cycle/course`,
                {
                  id: cat.id,
                  courseID: value.course,
                  categoryID: cat.category,
                  hours: cat.hours,
                },
              ),
        ),
      ])
        .then(() => dispatch(fetchRecertificationCycleCategories(cycleID)))
        .then(() => dispatch(refetchCycles))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          showError?.(
            pluckError(err, 'Error Creating Recertification Cycle Course'),
          )
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose} maxWidth="md">
      <Paper sx={{ width: 600, maxWidth: '100%', px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Add Course
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography>1. Select Course</Typography>
            <form.Field
              name="course"
              validators={{
                onChange: ({ value }) =>
                  !value || value === 'unselected'
                    ? 'Course is a required field'
                    : undefined,
              }}
              listeners={{
                onChange: ({ value }) => {
                  if (form.state.values.categories.length === 1) {
                    form.setFieldValue(
                      'categories[0].hours',
                      courses.find((c) => c.id === value)?.length?.toString() ||
                        '',
                    )
                    if (
                      form
                        .getFieldValue('categories')
                        .every((c) => c.category === 'unselected')
                    ) {
                      let sectionID = courses.find(
                        (c) => c.id === value,
                      )?.sectionID
                      let sectionName = sections.find(
                        (s) => s.id === sectionID,
                      )?.name
                      let match = categories.find((c) => c.name === sectionName)
                      if (match && sectionName) {
                        form.setFieldValue('categories[0].category', match.id)
                      }
                    }
                  }
                },
              }}
              children={(field) => {
                let selectedCourse = courses.find(
                  (c) => c.id === field.state.value,
                )
                return (
                  <>
                    <FormControl fullWidth>
                      <InputLabel id="course-section-filter-label">
                        Category
                      </InputLabel>
                      <Select
                        labelId="course-section-filter-label"
                        label="Category"
                        value={activeSectionFilter}
                        onChange={(e) => setSectionFilter(e.target.value)}
                        sx={{ bgcolor: 'background.paper' }}
                      >
                        <MenuItem value="all">All Categories</MenuItem>
                        {availableSections.map((section) => (
                          <MenuItem key={section.id} value={section.id}>
                            {section.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Autocomplete
                      value={courses.find((c) => field.state.value === c.id)}
                      onChange={(_, value) => {
                        field.handleChange(value?.id || '')
                      }}
                      open={courseListOpen}
                      onOpen={() => setCourseListOpen(true)}
                      onClose={() => {
                        if (detailCourse) return
                        // eslint-disable-next-line fp/no-mutation
                        keepCourseSearch.current = false
                        setCourseListOpen(false)
                      }}
                      inputValue={courseSearch}
                      onInputChange={(_, value, reason) => {
                        if (
                          keepCourseSearch.current &&
                          (reason === 'reset' || reason === 'blur')
                        )
                          return
                        // eslint-disable-next-line fp/no-mutation
                        keepCourseSearch.current = false
                        setCourseSearch(value)
                      }}
                      // The selected course stays an option so the input keeps showing it.
                      options={availableCourses.filter(
                        (c) =>
                          activeSectionFilter === 'all' ||
                          c.sectionID === activeSectionFilter ||
                          c.id === field.state.value,
                      )}
                      getOptionKey={(option) => option.id}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option) => {
                        // eslint-disable-next-line fp/no-rest-parameters
                        let { key, ...optionProps } = props
                        let sectionName = sections.find(
                          (s) => s.id === option.sectionID,
                        )?.name
                        return (
                          <Box
                            key={key}
                            component="li"
                            {...optionProps}
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <Typography sx={{ flex: '1 1 400px' }}>
                              {option.name}
                            </Typography>
                            {sectionName && (
                              <Tooltip title={sectionName}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={sectionName}
                                  sx={{ maxWidth: 120 }}
                                />
                              </Tooltip>
                            )}
                            <Chip
                              size="small"
                              color={option.completed ? 'success' : 'default'}
                              icon={
                                option.completed ? <CorrectIcon /> : undefined
                              }
                              label={`${option.length} Hour${option.length === 1 ? '' : 's'}`}
                            />
                            {option.publishedVersion === 2 && (
                              <Tooltip title="Course Details">
                                <IconButton
                                  sx={{ mx: -0.5, my: -1 }}
                                  size="small"
                                  aria-label={`${option.name} details`}
                                  // Keeps focus in the input so the list stays open
                                  // for the click, which mustn't select the option.
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    // eslint-disable-next-line fp/no-mutation
                                    keepCourseSearch.current = true
                                    setDetailCourse({
                                      id: option.id,
                                      name: option.name,
                                    })
                                  }}
                                >
                                  <InfoIcon fontSize="small" color="action" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={{ bgcolor: 'background.paper' }}
                          label="Course"
                          error={field.state.meta.errors.length > 0}
                          helperText={field.state.meta.errors.join(', ')}
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                    />
                    {/* The list's info buttons can't be reached by keyboard, so
                        the selected course's details are also offered here. */}
                    {selectedCourse?.publishedVersion === 2 && (
                      <Button
                        size="small"
                        startIcon={<InfoIcon />}
                        sx={{ alignSelf: 'flex-start' }}
                        onClick={() =>
                          setDetailCourse({
                            id: selectedCourse.id,
                            name: selectedCourse.name,
                          })
                        }
                      >
                        Course Details
                      </Button>
                    )}
                  </>
                )
              }}
            />
            <form.Subscribe
              selector={(state) => state.values.course}
              children={(co) => {
                let hours = courses.find((c) => c.id === co)?.length
                return (
                  <form.Field
                    name="categories"
                    mode="array"
                    validators={{
                      onSubmit: ({ value }) =>
                        value.reduce(
                          (sum, c) => sum + parseFloat(c.hours),
                          0,
                        ) !== hours
                          ? `All hours must be assigned & all category hours must add up to ${hours} hours.`
                          : undefined,
                    }}
                  >
                    {(field) => (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>
                            2. Assign {hours} Hour{hours === 1 ? '' : 's'}
                          </Typography>
                          <Button
                            color="secondary"
                            sx={{
                              mb: -0.25,
                            }}
                            size="small"
                            onClick={() => {
                              field.pushValue({
                                id: undefined,
                                category: 'unselected',
                                hours: '',
                              })
                            }}
                          >
                            Add Category
                          </Button>
                          <Button
                            color="error"
                            sx={{
                              mb: -0.25,
                            }}
                            size="small"
                            onClick={() =>
                              field.removeValue(field.state.value.length - 1)
                            }
                            disabled={field.state.value.length === 1}
                          >
                            Remove Category
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {field.state.value.map((_, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                              <form.Field
                                name={`categories[${i}].category`}
                                validators={{
                                  onSubmit: ({ value }) =>
                                    value === 'unselected'
                                      ? 'Category is a required field'
                                      : undefined,
                                }}
                                children={(subField) => (
                                  <FormControl
                                    fullWidth
                                    error={
                                      subField.state.meta.errors.length > 0
                                    }
                                  >
                                    <InputLabel
                                      id={`course-category-label-${i}`}
                                    >
                                      Category
                                    </InputLabel>
                                    <Select
                                      labelId={`course-category-label-${i}`}
                                      value={subField.state.value}
                                      label="Category"
                                      onChange={(e) =>
                                        subField.handleChange(e.target.value)
                                      }
                                      sx={{ bgcolor: 'background.paper' }}
                                    >
                                      <MenuItem value="unselected">
                                        Unselected
                                      </MenuItem>
                                      {categories
                                        .filter(
                                          (category) =>
                                            category.categoryType ===
                                              'standard' &&
                                            (category.id ===
                                              subField.state.value ||
                                              !field.state.value
                                                .map((c) => c.category)
                                                .includes(category.id)),
                                        )
                                        .toSorted((a, b) =>
                                          `${a.name}${a.subcategory || ''}`.localeCompare(
                                            `${b.name}${b.subcategory || ''}`,
                                          ),
                                        )
                                        .map((category) => (
                                          <MenuItem
                                            key={category.id}
                                            value={category.id}
                                          >
                                            {category.name}{' '}
                                            {category.subcategory &&
                                              `(${category.subcategory})`}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {subField.state.meta.errors.length > 0 && (
                                      <FormHelperText>
                                        {subField.state.meta.errors.join(', ')}
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                              <form.Field
                                name={`categories[${i}].hours`}
                                validators={{
                                  onSubmit: ({ value }) =>
                                    !parseFloat(value)
                                      ? 'Hours is a required field & must be greater than 0'
                                      : undefined,
                                }}
                                children={(subField) => (
                                  <TextField
                                    sx={{ bgcolor: 'background.paper' }}
                                    fullWidth
                                    variant="outlined"
                                    value={subField.state.value}
                                    label="Hours"
                                    name={subField.name}
                                    onChange={(e) => {
                                      subField.handleChange(
                                        e.target.value.replace(/[^\d.]/g, ''),
                                      )
                                      field.validate('change')
                                    }}
                                    onBlur={subField.handleBlur}
                                    error={
                                      subField.state.meta.errors.length > 0
                                    }
                                    helperText={subField.state.meta.errors.join(
                                      ', ',
                                    )}
                                  />
                                )}
                              />
                            </Box>
                          ))}
                          {field.state.meta.errors.length > 0 && (
                            <FormHelperText sx={{ color: 'error.main' }}>
                              {field.state.meta.errors.join(', ')}
                            </FormHelperText>
                          )}
                        </Box>
                      </>
                    )}
                  </form.Field>
                )
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
        <CourseDetailModal
          courseID={detailCourse?.id ?? null}
          courseName={detailCourse?.name ?? ''}
          onClose={() => setDetailCourse(null)}
        />
      </Paper>
    </Dialog>
  )
}
